import { useEffect, useRef } from 'react';

type TimerMode = 'interval' | 'timeout';

interface UseTimerOptions {
	callback: () => void | Promise<void>;
	delayMs: number;
	enabled?: boolean;
	mode?: TimerMode;
}

/** Schedules a callback while enabled and always clears the active browser timer on cleanup. */
export function useTimer({ callback, delayMs, enabled = true, mode = 'timeout' }: UseTimerOptions) {
	const callbackRef = useRef(callback);

	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	useEffect(() => {
		if (!enabled || delayMs <= 0) {
			return undefined;
		}

		const invoke = () => {
			void callbackRef.current();
		};

		if (mode === 'interval') {
			const intervalId = window.setInterval(invoke, delayMs);
			return () => window.clearInterval(intervalId);
		}

		const timeoutId = window.setTimeout(invoke, delayMs);
		return () => window.clearTimeout(timeoutId);
	}, [delayMs, enabled, mode]);
}
